import { Component, Input, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { FlyoutDataType, FlyoutOpeningData, PersonDetails, SessionDetails, VisitDevice } from '../flyout-models';
import { Subscription } from 'rxjs';
import { FlyoutService } from '../flyout.service';

@Component({
  selector: 'app-flyout-device-info',
  templateUrl: './flyout-device-info.component.html',
  styleUrls: ['./flyout-device-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutDeviceInfoComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  private sessionDetails: SessionDetails;
  private sessionSubscription: Subscription;
  public personDetails: PersonDetails;
  private personSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  public operatingSystems: Array<{ name: string; osType: string }>;
  public webBrowsers: Array<{ name: string; browserType: string }>;
  public nativeApps: Array<{ platform: string; osType: string }>;
  public screenResolutions: Array<string>;
  public expandedTab: string;

  private _openingData: FlyoutOpeningData;
  @Input() public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    this.expandedTab = this._openingData.expandedTab;
    this.processDeviceInfo();
  }

  public get flyoutOpeningData(): FlyoutOpeningData {
    return this._openingData;
  }

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
  ) {
    this.logger = loggerFactory.get('FlyoutCampaignInformationComponent');
  }

  ngOnInit() {
    this.sessionSubscription = this.flyoutService.sessionDetailsBS.subscribe((sessionDetails: SessionDetails) => {
      this.sessionDetails = sessionDetails;
      this.processDeviceInfo();
    });
    this.personSubscription = this.flyoutService.personDetailsBS.subscribe((personDetails: PersonDetails) => {
      this.personDetails = personDetails;
      this.processDeviceInfo();
    });
  }

  private processDeviceInfo() {
    let webBrowsers = undefined,
      screenResolutions = undefined,
      operatingSystems = undefined,
      nativeApps: Array<{ platform: string; osType: string }> = undefined;
    if (this._openingData.type === FlyoutDataType.Session && this.sessionDetails) {
      operatingSystems = [
        {
          name: this.sessionDetails.operatingSystem,
          osType: this.parseOsType(this.sessionDetails.operatingSystem),
        },
      ];
      const nativePlatform = this.detectNativePlatform(
        this.sessionDetails.operatingSystem,
        this.sessionDetails.webBrowser,
      );
      if (nativePlatform) {
        nativeApps = [{ platform: nativePlatform, osType: this.parseOsType(this.sessionDetails.operatingSystem) }];
      } else {
        webBrowsers = [
          {
            name: this.sessionDetails.webBrowser,
            browserType: this.parseBrowserType(this.sessionDetails.webBrowser),
          },
        ];
      }
      screenResolutions = [this.sessionDetails.screenResolution];
    } else if (this._openingData.type === FlyoutDataType.Contact && this.personDetails) {
      if (this.personDetails.devices) {
        ((webBrowsers = []), (screenResolutions = []), (operatingSystems = []), (nativeApps = []));
        this.personDetails.devices.forEach((device: VisitDevice) => {
          const nativePlatform = this.detectNativePlatform(device.operatingSystem, device.browser);
          if (nativePlatform) {
            if (nativeApps.findIndex((app) => app.platform === nativePlatform) === -1) {
              nativeApps.push({ platform: nativePlatform, osType: this.parseOsType(device.operatingSystem) });
            }
          } else if (webBrowsers.findIndex((browser) => browser.name === device.browser) === -1) {
            webBrowsers.push({
              name: device.browser,
              browserType: this.parseBrowserType(device.browser),
            });
          }
          if (operatingSystems.findIndex((os) => os.name === device.operatingSystem) === -1) {
            operatingSystems.push({
              name: device.operatingSystem,
              osType: this.parseOsType(device.operatingSystem),
            });
          }
          if (screenResolutions.indexOf(device.screenResolution) === -1) {
            screenResolutions.push(device.screenResolution);
          }
        });
      }
    }

    this.webBrowsers = webBrowsers;
    this.nativeApps = nativeApps;
    this.screenResolutions = screenResolutions;
    this.operatingSystems = operatingSystems;
  }

  // Native-app traffic has no real browser, so Tracker stores "Other" (or empty) for browser.
  private detectNativePlatform(operatingSystem: string, webBrowser: string): string | undefined {
    if (!operatingSystem) {
      return undefined;
    }
    const browser = (webBrowser || '').toLowerCase();
    if (browser && browser !== 'other') {
      return undefined;
    }
    const os = operatingSystem.toLowerCase();
    if (os.indexOf('android') !== -1) {
      return 'Android';
    }
    if (os.indexOf('ios') !== -1 || os.indexOf('iphone') !== -1 || os.indexOf('ipad') !== -1) {
      return 'iOS';
    }
    return undefined;
  }

  private parseBrowserType(webBrowser: string): string {
    if (!webBrowser) {
      return undefined;
    }
    webBrowser = webBrowser.toLowerCase();
    if (webBrowser.indexOf('chrome') !== -1) {
      return 'chrome';
    }
    if (webBrowser.indexOf('explorer') !== -1) {
      return 'explorer';
    }
    if (webBrowser.indexOf('edge') !== -1) {
      return 'edge';
    }
    if (webBrowser.indexOf('safari') !== -1) {
      return 'safari';
    }
    if (webBrowser.indexOf('firefox') !== -1) {
      return 'firefox';
    }
    if (webBrowser.indexOf('opera') !== -1) {
      return 'opera';
    }
    if (webBrowser.indexOf('blackberry') !== -1) {
      return 'blackberry';
    }
    return undefined;
  }

  private parseOsType(operatingSystem: string): string {
    if (!operatingSystem) {
      return undefined;
    }
    if (operatingSystem.toLowerCase().indexOf('windows') !== -1) {
      return 'windows';
    }
    if (
      operatingSystem.toLowerCase().indexOf('mac') !== -1 ||
      operatingSystem.toLowerCase().indexOf('iphone') !== -1 ||
      operatingSystem.toLowerCase().indexOf('ios') !== -1 ||
      operatingSystem.toLowerCase().indexOf('os x') !== -1
    ) {
      return 'apple';
    }
    if (operatingSystem.toLowerCase().indexOf('linux') !== -1) {
      return 'linux';
    }
    if (operatingSystem.toLowerCase().indexOf('android') !== -1) {
      return 'android';
    }
    if (operatingSystem.toLowerCase().indexOf('blackberry') !== -1) {
      return 'blackberry';
    }
    return undefined;
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.sessionSubscription, this.personSubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
